import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Trash2, Plus } from "lucide-react";
import { useState, useMemo } from "react";
import { SimpleClusterForm } from "@/types";
import { useFormikContext } from "formik";
import { Button } from "@/components/ui/button";
import { cn, getFormikEnvKeysValues } from "@/lib/utils";
import { TextField } from "@/components/forms/fields";
import { containerVerticalFlowAnalytics } from "../analytics";
import { SectionHeader } from "@/components/Common/SectionHeader/SectionHeader";

export const EnvironmentVariablesSection: React.FC = () => {
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const { values, setValues } = useFormikContext<SimpleClusterForm>();

  const { normalVariables, secretVariables } = useMemo(() => {
    const { normKeys, secretKeys } = getFormikEnvKeysValues(values);

    const parseIndex = (key: string) => {
      const match = key.match(/_(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    };

    const normal = normKeys
      .sort((a, b) => parseIndex(a) - parseIndex(b))
      .map((key) => {
        const index = parseIndex(key);
        return {
          id: key,
          key: key,
          value: `env_normal_value_${index}`
        };
      });

    const secret = secretKeys
      .sort((a, b) => parseIndex(a) - parseIndex(b))
      .map((key) => {
        const index = parseIndex(key);
        return {
          id: key,
          key: key,
          value: `env_private_value_${index}`
        };
      });

    return { normalVariables: normal, secretVariables: secret };
  }, [values]);

  const handleAddVariable = (type = "normal") => {
    const allKeys = Object.keys(values);
    let maxIndex = -1;
    const regex = /env_(?:normal|private)_key_(\d+)/;

    allKeys.forEach((k) => {
      const match = k.match(regex);
      if (match) {
        const idx = parseInt(match[1], 10);
        if (idx > maxIndex) maxIndex = idx;
      }
    });

    const newIndex = maxIndex + 1;
    const keyPrefix = type === "normal" ? "env_normal" : "env_private";

    const newKey = `${keyPrefix}_key_${newIndex}`;
    const newValue = `${keyPrefix}_value_${newIndex}`;

    setValues({
      ...values,
      [newKey]: "",
      [newValue]: ""
    });
    setIsOpenMenu(false);
    containerVerticalFlowAnalytics.trackEnvVarAdded(type === "normal" ? "normal" : "private");
  };

  const handleRemoveVariable = (key: string, value: string) => {
    const isPrivate = key.includes("env_private");
    const newValues = { ...values };
    delete newValues[key as keyof SimpleClusterForm];
    delete newValues[value as keyof SimpleClusterForm];
    setValues(newValues);
    containerVerticalFlowAnalytics.trackEnvVarRemoved(isPrivate ? "private" : "normal");
  };

  const handleValidate = (variableKey: string) => {
    if (!variableKey) return undefined;
    const { normKeys, secretKeys } = getFormikEnvKeysValues(values);
    const allKeys = [...normKeys, ...secretKeys];
    const filteredKeys = allKeys.filter(
      (key) => values[key as keyof SimpleClusterForm] === variableKey
    );
    return filteredKeys.length > 1 ? "Keys must be unique" : undefined;
  };

  const renderVariableRows = (
    variables: Array<{ id: string; key: string; value: string }>,
    type: "normal" | "private"
  ) =>
    variables.map((variable) => (
      <div className="flex gap-2 max-md:flex-col" key={variable.id}>
        <TextField
          name={variable.key}
          placeholder="Key"
          validate={handleValidate}
          baseHOCClassName="pb-0"
          onBlur={() => containerVerticalFlowAnalytics.trackEnvVarKeyFilled(type)}
        />
        <TextField
          name={variable.value}
          placeholder="Value"
          type={type === "private" ? "password" : "text"}
          baseHOCClassName="pb-0"
          onBlur={() => containerVerticalFlowAnalytics.trackEnvVarValueFilled(type)}
        />
        <Button
          type="button"
          variant={"outline"}
          onClick={() => handleRemoveVariable(variable.key, variable.value)}
          className="rounded px-3 py-2 text-red-light-200 hover:bg-red-dark-300/10 hover:text-red-light-200"
        >
          <Trash2 className="h-[16px] w-[16px]" />
        </Button>
      </div>
    ));

  const hasVariables = normalVariables.length > 0 || secretVariables.length > 0;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-row flex-wrap items-center justify-between gap-4">
        <SectionHeader
          title="Environment Variables"
          subtitle="Add environment variables"
          savedInConfig
        />
        <DropdownMenu open={isOpenMenu} onOpenChange={() => setIsOpenMenu((prev) => !prev)}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant={"outline"} className="gap-2 px-4 py-2">
              <Plus className="h-[16px] w-[16px]" />
              <span>Add</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="flex min-w-[100px] flex-col gap-2 rounded border-border p-1"
          >
            <DropdownMenuItem
              className="h-[32px] items-center gap-2 p-2"
              onClick={(e) => {
                e.stopPropagation();
                handleAddVariable();
              }}
            >
              <span>Normal Variable</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="h-[32px] items-center gap-2 p-2"
              onClick={(e) => {
                e.stopPropagation();
                handleAddVariable("private");
              }}
            >
              <span>Private Variable</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {hasVariables && (
        <div className="w-full rounded-lg border border-border">
          <div className="flex flex-col gap-6 p-4">
            {normalVariables.length > 0 && (
              <div className="flex flex-col gap-4">
                <div className="text-sm font-medium text-foreground">Normal Variables</div>
                {renderVariableRows(normalVariables, "normal")}
              </div>
            )}
            {secretVariables.length > 0 && (
              <div className={cn("flex flex-col gap-4", normalVariables.length > 0 && "pt-2")}>
                <div className="text-sm font-medium text-foreground">Private Variables</div>
                {renderVariableRows(secretVariables, "private")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
